import { IDField, IDogeFields, IDogeFieldsOptional } from 'src/typings/global-config';
import { IDeveloper, IFileUri } from 'src/typings/complexes';
import { LocationButtonsEnum } from 'src/components/profileComponents/Marketplace/components/Filters/constants';
import { IPlacementPropertyBase, ITranslate } from 'src/typings/viewConfig';
import {
  IMarketplaceConfigAvailabilityCounter,
  IMarketplaceConfigHeader,
  IMarketplaceConfigLocation,
  IMarketplaceConfigMinMaxRange,
  IMarketplaceConfigProjectTimeline,
  IMarketplaceConfigUnitsBreakDown, IMarketplacePlacementCard, IMarketplacePlacementTileSortOrderProperty,
} from 'src/typings/marketplaceTile';
import { Option as AppOption } from 'src/typings/app';

export interface IMarketplace extends IDogeFields {
  id: string;
  name: { [ket: string]: string },
  shortUniqueName: string,
  developers: IDeveloper[],
  language: string,
  secondaryLanguages: string[],
  frontConfiguration: {
    module: IMarketplaceModule
  },
  complexProperties: IComplexProperty[]
  placementProperties: IMarketplacePlacementProperty[],
}

export interface IComplexProperty extends IDogeFields {
  id: number;
  diType: string;
  systemName: string,
  oldSystemName: string | null,
  translates: IFieldWithTranslation | null
}

export interface IMarketplacePlacementProperty extends IPlacementPropertyBase {
  diType: 'PlacementProperty'
}

export interface IMarketplaceModuleFiltersConfigurationElementsConf extends IDogeFieldsOptional {
  headerTitle: IMarketplaceModulePageElement,
  clearFiltersButton: IMarketplaceModulePageElement,
  applyUnitsFiltersButton: IMarketplaceModulePageElement,
  applyProjectsFiltersButton: IMarketplaceModulePageElement,
}

export interface IMarketplaceModuleFiltersConfiguration {
  primarySection: IMarketplaceModulePrimarySection,
  secondaries: IMarketplaceModulePrimarySectionType[],
  secondaryFiltersElementsConf: IMarketplaceModuleFiltersConfigurationElementsConf | null,
  tooltipIconUri: IFileUri | null,
}
export interface IMarketplaceAccessUrl extends IDogeFields {
  id: number,
  url: string;
}

export interface IMarketplacePlacementTileSortOrder extends IDogeFieldsOptional {
  isDefault: boolean;
  property: IMarketplacePlacementTileSortOrderProperty | null;
  sortOrderDirection: SortOrderType | null;
  valueName: ITranslate | null;
}

export interface IMarketplacePlacementTilePropertiesSortOrder extends IDField {
  isActive: boolean;
  elements: IMarketplacePlacementTileSortOrder[],
}

export interface IMarketplacePlacementTile extends IDogeFields, IDField {
  card: IMarketplacePlacementCard,
  propertiesSortOrder: IMarketplacePlacementTilePropertiesSortOrder,
}

export interface IMarketplaceComplexTile extends IDogeFields, IDField {
  header: IMarketplaceConfigHeader,
  location: IMarketplaceConfigLocation,
  minMaxRange: IMarketplaceConfigMinMaxRange,
  projectTimeline: IMarketplaceConfigProjectTimeline,
  unitsBreakdown: IMarketplaceConfigUnitsBreakDown,
  availabilityCounter: IMarketplaceConfigAvailabilityCounter,
  detailsButton: IMarketplaceModulePageElement,
}

export type IMarketplaceModulePageElement = IDogeFieldsOptional & { name: ITranslate | null };

export interface IMarketplaceModulePageElementsConf extends IDogeFieldsOptional {
  allProjectsSearchText: ITranslate | null,
  successfulCopyLinkText: ITranslate | null,
  buttonsConf: {
    allFiltersButton: IMarketplaceModulePageElement,
    projectsButton: IMarketplaceModulePageElement,
    unitsButton: IMarketplaceModulePageElement,
    mapButton: IMarketplaceModulePageElement,
  }
  paginationText: {
    itemNamePlural: ITranslate | null,
    ofText: ITranslate | null,
  }
}

export interface IMarketplaceModule extends IDogeFieldsOptional, IDField {
  filtersConfiguration: IMarketplaceModuleFiltersConfiguration;
  clientAccessUrls: IMarketplaceAccessUrl[] | null;
  complexTile: IMarketplaceComplexTile
  placementTile: IMarketplacePlacementTile
  pageElementsConf: IMarketplaceModulePageElementsConf | null
}

export interface IMarketplaceModulePrimarySection {
  selections: IMarketplaceModulePrimarySectionType[];
  tag: IMarketplaceFilterTagSelectedValues;
  selectionButtonsConf: {
    clearButton: IMarketplaceModulePageElement
    submitButton: IMarketplaceModulePageElement
  } | null

}
export interface IMarketplaceFilterBase {
  id: string,
  type: IMarketplaceFilterType
  sortOrder: number,
  name: IFieldWithTranslation | null,
  callToActionText: IFieldWithTranslation | null,
  accordionState: AccordionStateType | null,
  isAccordionOpen?: boolean
}

export interface IMarketplaceToolTip {
  text: ITranslate | null
  isActive: boolean
}

export interface IMarketplaceFilterLocationFilter extends IMarketplaceFilterBase {
  type: IMarketplaceFilterType.NestedAllValuesTag,
  subType?: LocationButtonsEnum,
  property: IMarketplaceProperty | null,
  propertyValuesOrder: SortOrderType | null,
  subFilters: IMarketplaceFilterLocationFilter[]
}

export interface IMarketplaceFilterLocation extends IMarketplaceFilterBase {
  filters: IMarketplaceFilterLocationFilter[]
  tooltip: IMarketplaceToolTip | null,
}

export interface IMarketplaceFilterTagSelectedValues extends IMarketplaceFilterBase {
  selectedProperties: IMarketplaceFilterTagSelectedValueSelectedProperty[];
  tooltip: IMarketplaceToolTip | null,
}

export interface IMarketplaceFilterTagAllValues extends IMarketplaceFilterBase{
  property: IMarketplaceProperty | null,
  propertyValuesOrder: SortOrderType | null
  tooltip: IMarketplaceToolTip | null,
}

export interface IMarketplaceFilterCheckboxAllValues extends IMarketplaceFilterBase {
  property: IMarketplaceProperty | null,
  propertyValuesOrder: SortOrderType | null
  tooltip: IMarketplaceToolTip | null,
}

export interface IMarketplaceFilterRange extends IMarketplaceFilterBase {
  fromName?: IFieldWithTranslation,
  toName?: IFieldWithTranslation,
  property: IMarketplaceProperty | null,
  mainUom?: IFieldWithTranslation,
  secondaryUom?: IFieldWithTranslation,
  uomRate?: number
  tooltip: IMarketplaceToolTip | null,
}

export interface IFieldWithTranslation {
  [key: string]: string;
}

export interface IMarketplaceProperty {
  '@id'?: string,
  systemName: string,
  oldSystemName: string | null,
  translates: IFieldWithTranslation | null
}

export interface IMarketplaceFilterTagSelectedValueSelectedProperty {
  id: string,
  sortOrder: number,
  property: IMarketplaceProperty | null,
  value: IFieldWithTranslation,
  iconFileUri: IFileUri | null,
}

export interface IMarketplaceLocationRequestFilter {
  propertyName: string;
  oldSystemName: string | null;
  equals: string
}

export interface IMarketplaceRangeRequestFilter {
  propertyName: string;
  oldSystemName: string | null;
  min: number,
  max: number,
}

export interface IMarketplaceStaticLinkParams {
  param: string,
  value: string,
}

export type IMarketplaceRequestFilter =
  IMarketplaceLocationRequestFilter
| IMarketplaceRangeRequestFilter

export interface IWhereConditionsLocation {
  propertyName: string;
  equals: string
}

export interface IWhereConditionsRange {
  propertyName: string;
  min: number,
  max: number,
}

export interface IViewTileSortOrder {
  id?: string,
  propertySystemName: string | null,
  orderDirection: string | null,
}

export interface IViewTile {
  type: IViewTileType,
  pageNumber: number,
  sortOrders?: IViewTileSortOrder[] | []
}

export interface IGeneralUnitInfo {
  complexesTotalCount: number,
  placementsTotalCount: number,
}

export interface IMarketplacePagination {
  current: number,
  first: number,
  last: number,
  next: number,
  previous: number,
  itemsPerPage: number,
  type: IViewTileType
}

export interface IMarketplaceColumnsGrid {
  type: IViewTileType, value: number
}

export type IWhereConditions = IWhereConditionsLocation | IWhereConditionsRange;

export type IFiltersSubtitle = {
  [key: string]: string[],
}

export type IMarketplaceModulePrimarySectionType =
  IMarketplaceFilterLocation
  | IMarketplaceFilterTagSelectedValues
  | IMarketplaceFilterTagAllValues
  | IMarketplaceFilterRange
  | IMarketplaceFilterCheckboxAllValues

export enum IViewTileType {
  Placements = 'placements',
  Complexes = 'complexes',
  Map = 'complexesMap',
}

export type IViewType = 'units' | 'projects' | 'map';

export const ViewTileTypeMapping: {[key in IViewTileType]: IViewType} = {
  [IViewTileType.Placements]: 'units',
  [IViewTileType.Complexes]: 'projects',
  [IViewTileType.Map]: 'map',
};

export const ViewTileTypeRouteMapping = {
  units: IViewTileType.Placements,
  projects: IViewTileType.Complexes,
  map: IViewTileType.Map,
};

export const IViewTileTypeArray = Object.values(ViewTileTypeMapping);

export enum IMarketplaceFilterType {
  GroupingFilter = 'GroupingFilter',
  AllValuesRange = 'AllValuesRange',
  AllValuesCheckbox = 'AllValuesCheckbox',
  AllValuesTag = 'AllValuesTag',
  NestedAllValuesTag = 'NestedAllValuesTag',
  SelectedValuesTag = 'SelectedValuesTag',
  ObjectFilter = 'ObjectFilter',
}

export interface Option<T> extends AppOption<T> {}

export interface NumberTransformationConfig extends IDogeFields, IDField {
  precision: RoundingMethods,
  thousandsSeparator: ThousandsSeparator,
}

export enum IPlacementHeaderMineElementType {
  MAIN = 'Main',
  SECONDARY = 'Secondary',
}

export type AccordionStateType = 'none' | 'open' | 'closed';
export const accordionStateOptions = [{ label: 'none', value: 'none' }, { label: 'open', value: 'open' }, { label: 'closed', value: 'closed' }];
export type SortOrderType = 'A-Z_0-9' | '0-9_A-Z' | 'object.id_0-9' | 'object.id_9-0' | 'object.sortOrder_0-9' | 'object.sortOrder_9-0';
export type SortOrderTypeFull = SortOrderType & '9-0_Z-A' | 'Z-A_9-0';
export const sortOrderOptions = [
  { label: 'A -> Z, then 1 -> 9', value: 'A-Z_0-9' },
  { label: '1 -> 9, then A -> Z', value: '0-9_A-Z' },
  { label: 'By ID (S -> L)', value: 'object.id_0-9' },
  { label: 'By ID (L -> S)', value: 'object.id_9-0' },
  { label: 'object.sortOrder_0-9', value: 'object.sortOrder_0-9' },
  { label: 'object.sortOrder_9-0', value: 'object.sortOrder_9-0' },
];

export const sortOrderOptionsFull = [
  ...sortOrderOptions,
  { label: '9 -> 1, then Z -> A', value: '9-0_Z-A' },
  { label: 'Z -> A, then 9 -> 1', value: 'Z-A_9-1' },
];

export const sortOrderOptionsNotProjectName = [
  { label: 'A -> Z, then 1 -> 9', value: 'A-Z_0-9' },
  { label: '1 -> 9, then A -> Z', value: '0-9_A-Z' },
  { label: 'object.sortOrder_0-9', value: 'object.sortOrder_0-9' },
  { label: 'object.sortOrder_9-0', value: 'object.sortOrder_9-0' },
];

export const sortOrderOptionsMap = {
  'A-Z_0-9': 'A -> Z, then 1 -> 9',
  '0-9_A-Z': '1 -> 9, then A -> Z',
  'object.id_0-9': 'By ID (S -> L)',
  'object.id_9-0': 'By ID (L -> S)',
  'object.sortOrder_0-9': 'object.sortOrder_0-9',
  'object.sortOrder_9-0': 'object.sortOrder_9-0',
};

export const sortOrderOptionsMapFull = {
  ...sortOrderOptionsMap,
  '9-0_Z-A': '9 -> 1, then Z -> A',
  'Z-A_9-1': 'Z -> A, then 9 -> 1',
};

export enum RoundingMethods {
  NONE = -1,
  WHOLE_NUMBER = 0,
  ONE_DECIMAL = 1,
  TWO_DECIMAL = 2,
}

export const roundingMethodsOptions = [
  { label: 'None(Default)', value: RoundingMethods.NONE },
  { label: 'Whole number', value: RoundingMethods.WHOLE_NUMBER },
  { label: '1 Decimal', value: RoundingMethods.ONE_DECIMAL },
  { label: '2 Decimal', value: RoundingMethods.TWO_DECIMAL },
];

export const roundingMethodsOptionsMap = {
  [RoundingMethods.NONE]: 'None(Default)',
  [RoundingMethods.WHOLE_NUMBER]: 'Whole number',
  [RoundingMethods.ONE_DECIMAL]: '1 Decimal',
  [RoundingMethods.TWO_DECIMAL]: '2 Decimal',
};

export enum ThousandsSeparator {
  NONE = '',
  COMMA = ',',
  DOT = '.',
  SPACE = ' ',
  APOSTROPHE = '\'',
}

export const thousandsSeparatorOptions = [
  { label: 'None(Default)', value: ThousandsSeparator.NONE },
  { label: 'Space', value: ThousandsSeparator.SPACE },
  { label: 'Comma', value: ThousandsSeparator.COMMA },
  { label: 'Dot', value: ThousandsSeparator.DOT },
  { label: 'Apostrophe', value: ThousandsSeparator.APOSTROPHE },
];

export const thousandsSeparatorOptionsMap = {
  [ThousandsSeparator.NONE]: 'None(Default)',
  [ThousandsSeparator.SPACE]: 'Space',
  [ThousandsSeparator.COMMA]: 'Comma',
  [ThousandsSeparator.DOT]: 'Dot',
  [ThousandsSeparator.APOSTROPHE]: 'Apostrophe',
};

export const fontWeightOptions: Option<string>[] = [
  { label: 'Thin', value: '100' },
  { label: 'UltraLight', value: '200' },
  { label: 'Light', value: '300' },
  { label: 'Normal', value: '400' },
  { label: 'Medium', value: '500' },
  { label: 'SemiBold', value: '600' },
  { label: 'Bold', value: '700' },
  { label: 'ExtraBold', value: '800' },
  { label: 'Heavy', value: '900' },
];

export const textDecorationOptions: Option<string>[] = [
  { label: 'none', value: 'none' },
  { label: 'line through', value: 'line-through' },
];
